import React, { useState, useMemo } from 'react';
import axios from 'axios';
import { PencilSimple, Lock } from '@phosphor-icons/react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { useFleetOptions, buildVehiculoHandler, buildConductorHandler } from '@/hooks/useFleetOptions';
import { MovilizacionInfoGrid } from './MovilizacionInfoGrid';
import { AsignacionDisplay } from './AsignacionDisplay';
import { FleetSelectFields } from './FleetSelectFields';
import { MovilizacionStatusHeader } from './MovilizacionStatusHeader';
import { CierreButtons, CierreDialog } from './CierreDialog';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CoordActionButton = ({ movilizacion, isEditable, onEdit }) => {
  if (!isEditable) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 bg-[#F1F3F5] border border-[#E9ECEF]">
        <Lock size={14} weight="bold" className="text-[#6C757D]" />
        <span className="text-xs text-[#6C757D] font-['IBM_Plex_Sans']">Solo lectura</span>
      </div>
    );
  }
  return (
    <Button
      data-testid="editar-asignacion-button"
      onClick={onEdit}
      size="sm"
      className="gap-2 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] transition-colors"
    >
      <PencilSimple size={16} weight="bold" />
      {movilizacion.vehiculo ? 'Editar Asignación' : 'Asignar Vehículo'}
    </Button>
  );
};

const CoordAsignacionDialog = ({ open, onOpenChange, onSubmit, loading, formData, setFormData, fleetProps }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-md bg-white border border-[#E9ECEF]">
      <DialogHeader>
        <DialogTitle className="text-2xl font-black font-['Chivo'] tracking-tight text-[#0A0A0A]">
          Asignación de Transporte
        </DialogTitle>
        <DialogDescription className="text-[#495057] font-['IBM_Plex_Sans']">
          Asigna o edita el vehículo, conductor y teléfono de contacto
        </DialogDescription>
      </DialogHeader>

      <form data-testid="coord-asignacion-form" onSubmit={onSubmit} className="space-y-4 mt-4">
        <FleetSelectFields
          formData={formData}
          setFormData={setFormData}
          telefonoRequired
          testIdPrefix="coord"
          telefonoTestId="coord-telefono-input"
          {...fleetProps}
        />

        <div className="flex gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="flex-1 border-[#E9ECEF] hover:bg-[#F8F9FA] font-['IBM_Plex_Sans']"
          >
            Cancelar
          </Button>
          <Button
            data-testid="coord-confirm-button"
            type="submit"
            disabled={loading}
            className="flex-1 bg-[#C1121F] hover:bg-[#8B0000] text-white font-['IBM_Plex_Sans'] transition-colors"
          >
            {loading ? 'Guardando...' : 'Guardar Asignación'}
          </Button>
        </div>
      </form>
    </DialogContent>
  </Dialog>
);

export const CoordinadorMovilizacionCard = ({ movilizacion, onUpdate }) => {
  const [showDialog, setShowDialog] = useState(false);
  const [cierreAction, setCierreAction] = useState(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    vehiculo: movilizacion.vehiculo || '',
    conductor: movilizacion.conductor || '',
    telefono_conductor: movilizacion.telefono_conductor || '',
    vehiculo_id: movilizacion.vehiculo_id || '',
    conductor_id: movilizacion.conductor_id || '',
  });

  const { vehiculos: vehiculosOpts, conductores: conductoresOpts } = useFleetOptions({
    enabled: showDialog,
    fechaInicio: movilizacion.fecha_inicio,
    fechaFin: movilizacion.fecha_fin,
    excludeMovId: movilizacion.id,
  });

  const onVehiculoSelect = buildVehiculoHandler(vehiculosOpts, setFormData);
  const onConductorSelect = buildConductorHandler(conductoresOpts, setFormData);

  const fleetProps = useMemo(
    () => ({ vehiculosOpts, conductoresOpts, onVehiculoSelect, onConductorSelect }),
    [vehiculosOpts, conductoresOpts, onVehiculoSelect, onConductorSelect]
  );

  const isEditable = movilizacion.estado === 'en_espera_asignacion' || movilizacion.estado === 'asignado';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.patch(
        `${API_URL}/api/movilizaciones/${movilizacion.id}/asignacion`,
        {
          vehiculo: formData.vehiculo,
          conductor: formData.conductor,
          telefono_conductor: formData.telefono_conductor,
          vehiculo_id: formData.vehiculo_id || null,
          conductor_id: formData.conductor_id || null,
        },
        { withCredentials: true }
      );
      toast.success('Asignación actualizada exitosamente');
      setShowDialog(false);
      onUpdate();
    } catch (error) {
      const detail = error.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div
        data-testid={`coord-movilizacion-card-${movilizacion.id}`}
        className="bg-white border border-[#E9ECEF] p-6 hover:border-[#0A0A0A] transition-all"
      >
        <div className="flex items-start justify-between mb-4 gap-3">
          <MovilizacionStatusHeader movilizacion={movilizacion} />
          <div className="flex flex-wrap gap-2 justify-end">
            <CoordActionButton
              movilizacion={movilizacion}
              isEditable={isEditable}
              onEdit={() => setShowDialog(true)}
            />
            <CierreButtons movilizacion={movilizacion} onOpen={setCierreAction} testIdPrefix="coord" />
          </div>
        </div>

        <MovilizacionInfoGrid movilizacion={movilizacion} />
        <AsignacionDisplay
          movilizacion={movilizacion}
          showTelefonoSeparate
          title="Asignación de Transporte"
        />

        {movilizacion.comentario_admin && (
          <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">Comentario del Administrador</p>
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.comentario_admin}</p>
          </div>
        )}

        {movilizacion.comentario_cierre && (
          <div className="mt-4 pt-4 border-t border-[#E9ECEF]">
            <p className="text-xs text-[#6C757D] font-['IBM_Plex_Sans'] mb-1">
              Cierre {movilizacion.cerrado_por ? `por ${movilizacion.cerrado_por}` : ''}
            </p>
            <p className="text-sm font-['IBM_Plex_Sans'] text-[#0A0A0A]">{movilizacion.comentario_cierre}</p>
          </div>
        )}
      </div>

      <CoordAsignacionDialog
        open={showDialog}
        onOpenChange={setShowDialog}
        onSubmit={handleSubmit}
        loading={loading}
        formData={formData}
        setFormData={setFormData}
        fleetProps={fleetProps}
      />

      <CierreDialog
        movilizacion={movilizacion}
        cierreAction={cierreAction}
        open={!!cierreAction}
        onOpenChange={(v) => !v && setCierreAction(null)}
        onSuccess={onUpdate}
      />
    </>
  );
};
